import { stringify } from 'query-string';
import { useLocation } from 'react-router';
import {
  flowIdRiskAnalysisBE,
  flowIdRiskAnalysisNL,
  flowIdRiskAnalysisShortBE,
} from '../../../flows/src/constants/flowIds';
import usePartyId from '../../../common/src/hooks/query-params/usePartyId';
import useIsNLplan from '../../../common/src/hooks/useIsNLPlan';
import { useFlowsSelector } from '../../../common/src/utils/globalVariables';
import useRiskAnalysisPayload from './useRiskAnalysisPayload';
import useTracking from '../../../common/src/hooks/tracking/useTracking';
import { useLouiseDispatch } from '../../../sales-conversation/src/store/redux/store';
import { CreateFlowPayload } from '../../../flows/src/types/ApiTypes';
import { setSubjects } from '../store/redux/slices/selectionSlice';
import { SubjectTypes } from '../../../../types/RiskAnalaysis';
import useFlowsInitNew from '../../../flows/src/hooks/init/useFlowsInitNew';

const useStartRiskAnalysisFlow = (): ((
  uuid?: string,
  resumedSession?: boolean,
  customFlowId?: string,
) => Promise<void>) => {
  const { initFlow } = useFlowsInitNew();
  const { getFlowPayload } = useRiskAnalysisPayload();
  const stateFlowId = useFlowsSelector((state) => state.flows.metaData.flowId);
  const { pathname } = useLocation();
  const { isNL } = useIsNLplan();
  const partyId = usePartyId();
  const { trackEvent } = useTracking();
  const dispatch = useLouiseDispatch();

  /**
   * Because of the bad design of how sales conversation works, we have
   * to store a selection state for it to work correctly later in the flow.
   */
  const setSelectionState = (flowPayload: CreateFlowPayload) => {
    if (flowPayload.subject)
      dispatch(setSubjects(flowPayload.subject as SubjectTypes[]));
  };

  return async (
    uuid?: string,
    resumedSession?: boolean,
    customFlowId?: string,
  ) => {
    const _flowId = isNL ? flowIdRiskAnalysisNL : flowIdRiskAnalysisBE;
    const flowPayload = await getFlowPayload(uuid, resumedSession);
    setSelectionState(flowPayload);

    // Tracking
    if (
      (customFlowId || stateFlowId || _flowId) === flowIdRiskAnalysisShortBE
    ) {
      trackEvent('Start Short Risk Analysis');
    } else if (!isNL) {
      trackEvent('Start Long Risk Analysis');
    }

    const queryString = stringify({
      session_id: uuid,
      ...(partyId && { party_id: partyId }),
    });

    await initFlow({
      flowId: customFlowId || stateFlowId || _flowId,
      payload: flowPayload,
      inputUrl: `${pathname}?${queryString}`,
      outputUrl: `/risk-analysis/recommendations/finalize`,
      sessionId: uuid,
    });
  };
};

export default useStartRiskAnalysisFlow;
